import React, { useState, useEffect, useRef } from 'react';
// Import the JSON-based configuration
import appConfig from '../config/appConfig';

const UpdateStatusDialog = ({
  isOpen,
  onClose,
  selectedUserIds,
  corporateAccount,
  selectedAccountId,
  login_token,
  permissions,
  currentProject
}) => {
  const [userStatus, setUserStatus] = useState('');
  const [error, setError] = useState('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const statusInputRef = useRef(null);

  // Updated dialog styles with fixed layout
  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '500px',
      maxWidth: '90vw',
      height: 'auto',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '10px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 120px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  // Fetch statuses for the status dropdown
  const fetchStatuses = async () => {
    if (!corporateAccount || !login_token) return;

    setLoadingStatuses(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id:  "ALL PROJECTS",
          entity: "USER_STATUS"
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setStatuses(data.entity_statuses || []);
      } else {
        console.error('Failed to fetch statuses:', data.status_description);
        setStatuses([]);
      }
    } catch (error) {
      console.error('Error fetching statuses:', error);
      setStatuses([]);
    } finally {
      setLoadingStatuses(false);
    }
  };

  // Focus the status field when dialog opens
  useEffect(() => {
    if (isOpen && statusInputRef.current && !loadingStatuses) {
      setTimeout(() => {
        statusInputRef.current.focus();
      }, 100);
    }
  }, [isOpen, loadingStatuses]);

  // Reset form when dialog opens and fetch statuses
  useEffect(() => {
    if (isOpen) {
      setUserStatus('');
      setError('');
      setShowValidationErrors(false);
      fetchStatuses();
    }
  }, [isOpen, corporateAccount, currentProject]);

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    setShowValidationErrors(true);
    setError('');
    setIsLoading(true);

    // Validation checks
    if (!userStatus.trim()) {
      setError('User Status is required');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/update_user_status'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_ids: Array.from(selectedUserIds),
          user_status: userStatus,
          requesting_for_corporate_account: selectedAccountId
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setError('User status updated successfully');

        // Delay closing to show success message
        setTimeout(() => {
          onClose(true);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to update user status');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={dialogStyles.overlay}>
      <div style={dialogStyles.dialog}>
        {/* Fixed Header */}
        <div style={dialogStyles.header}>
          Update Status ({selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''})
        </div>

        {/* Scrollable Content */}
        <div style={dialogStyles.content}>
          <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1 }}>
              {/* Selected Users Display */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Selected Users ({selectedUserIds.size}):
                </label>
                <div style={{
                  maxHeight: '100px',
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  padding: '8px',
                  backgroundColor: '#f9fafb'
                }}>
                  {Array.from(selectedUserIds).map(userId => (
                    <div key={userId} style={{ fontSize: '12px', marginBottom: '2px' }}>
                      • {userId}
                    </div>
                  ))}
                </div>
              </div>

              {/* User Status */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  New Status: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                {loadingStatuses ? (
                  <div style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <svg style={{
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px',
                      width: '16px',
                      height: '16px'
                    }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading statuses...
                  </div>
                ) : (
                  <select
                    ref={statusInputRef}
                    value={userStatus}
                    onChange={(e) => setUserStatus(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: `1px solid ${showValidationErrors && !userStatus ? '#dc2626' : '#ddd'}`,
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                    required
                  >
                    <option value="">Select a status</option>
                    {statuses.map(status => (
                      <option key={status.status} value={status.status}>
                        {status.status}
                      </option>
                    ))}
                  </select>
                )}
                {showValidationErrors && !userStatus && (
                  <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    User Status is required
                  </span>
                )}
                {statuses.length === 0 && !loadingStatuses && (
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '4px'
                  }}>
                    No statuses available
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Fixed Footer */}
        <div style={dialogStyles.footer}>
          {/* Error message */}
          {error && (
            <div style={{
              marginBottom: '15px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
              color: error.includes('successfully') ? '#059669' : '#dc2626',
              border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <button
              type="button"
              onClick={() => onClose(false)}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={isLoading}
            >
              Close
            </button>

            <button
              type="submit"
              onClick={(e) => {
                if (!permissions.canUpdate) {
                  e.preventDefault();
                  setError('You do not have permission to update user status');
                  return;
                }
                handleSubmit(e);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: !permissions.canUpdate ? '#f3f4f6' : '#C8F4DE',
                color: !permissions.canUpdate ? '#6b7280' : 'black',
                border: !permissions.canUpdate ? '1px solid #d1d5db' : 'none',
                borderRadius: '4px',
                fontSize: '14px',
                opacity: isLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center'
              }}
              disabled={isLoading}
            >
              {isLoading && (
                <svg style={{
                  animation: 'spin 1s linear infinite',
                  marginLeft: '-4px',
                  marginRight: '8px',
                  width: '16px',
                  height: '16px'
                }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {!permissions.canUpdate ?
                'No Permission to Update' :
                (isLoading ? 'Updating Status...' : 'Update Status')
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateStatusDialog;